import { useState, useRef, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  IconButton,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import CloseIcon from '@mui/icons-material/Close';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { useDispatch } from 'react-redux';
import { cloneTemplate } from '../../../../actions/templates';
import { MAX_CONTACTS_PER_TEMPLATE } from '../constants';

const randomSuffix = () => Math.random().toString(36).substring(2, 8);
const TIMEOUT_MS = 30000;

const getContactsInBlock = (blockIndex, totalVersions, totalContacts) => {
  if (blockIndex < totalVersions - 1) {
    return MAX_CONTACTS_PER_TEMPLATE;
  }
  return totalContacts - (totalVersions - 1) * MAX_CONTACTS_PER_TEMPLATE;
};

const PHASES = {
  IDLE: 'idle',
  CLONING: 'cloning',
  CREATING: 'creating',
  SUCCESS: 'success',
  ERROR: 'error',
};

const BLOCK_STATUS = {
  PENDING: 'pending',
  CREATING: 'creating',
  CREATED: 'created',
  ERROR: 'error',
  SKIPPED: 'skipped',
};

export const CampaignCreationProgressModal = ({
  open,
  onComplete,
  onClose,
  onCampaignCreated,
  selectedTemplate,
  requiredVersions,
  totalContactCount,
  name,
  gupshupAppId,
}) => {
  const dispatch = useDispatch();
  const [phase, setPhase] = useState(PHASES.IDLE);
  const [templateBlocks, setTemplateBlocks] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [campaignName, setCampaignName] = useState('');
  const [clonedCount, setClonedCount] = useState(0);
  const startedRef = useRef(false);
  const cancelCloneRef = useRef(false);
  const creatingTimeoutRef = useRef(null);

  const resetState = useCallback(() => {
    setPhase(PHASES.IDLE);
    setTemplateBlocks([]);
    setErrorMessage('');
    setCampaignName('');
    setClonedCount(0);
    startedRef.current = false;
    cancelCloneRef.current = false;
    if (creatingTimeoutRef.current) {
      clearTimeout(creatingTimeoutRef.current);
      creatingTimeoutRef.current = null;
    }
  }, []);

  const cloneSingleBlock = useCallback(async (blockIndex, blocks) => {
    const contactsInBlock = getContactsInBlock(blockIndex, requiredVersions - 1, totalContactCount);
    const versionName = `${selectedTemplate.name}_Bloque${blockIndex + 2}_${contactsInBlock}cts_${randomSuffix()}`;

    setTemplateBlocks((prev) =>
      prev.map((b, idx) => (idx === blockIndex ? { ...b, status: BLOCK_STATUS.CREATING } : b))
    );

    const payload = {
      new_name: versionName,
      language_code: selectedTemplate.language || 'en_US',
      category: selectedTemplate.category,
    };

    const result = await Promise.race([
      dispatch(
        cloneTemplate({
          templateId: selectedTemplate._id,
          username: name,
          apiKey: gupshupAppId,
          payload,
        })
      ),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Tiempo de espera agotado (30s)')), TIMEOUT_MS)
      ),
    ]);

    if (result?.success) {
      const tName = result.data?.template_name || null;
      setTemplateBlocks((prev) =>
        prev.map((b, idx) =>
          idx === blockIndex ? { ...b, status: BLOCK_STATUS.CREATED, templateName: tName } : b
        )
      );
      return true;
    }
    throw new Error(result?.error || 'Error al crear el template');
  }, [selectedTemplate, totalContactCount, dispatch, name, gupshupAppId, requiredVersions]);

  const cloneAllTemplates = useCallback(async () => {
    const versionsToCreate = requiredVersions - 1;
    if (versionsToCreate <= 0) return 0;

    cancelCloneRef.current = false;

    const initialBlocks = [];
    for (let i = 0; i < versionsToCreate; i++) {
      const contactsInBlock = getContactsInBlock(i, versionsToCreate, totalContactCount);
      const versionName = `${selectedTemplate.name}_Bloque${i + 2}_${contactsInBlock}cts_${randomSuffix()}`;
      initialBlocks.push({ name: versionName, status: BLOCK_STATUS.PENDING });
    }
    setTemplateBlocks(initialBlocks);

    let created = 0;
    for (let i = 0; i < versionsToCreate; i++) {
      if (cancelCloneRef.current) {
        setTemplateBlocks((prev) =>
          prev.map((b, idx) => (idx >= i ? { ...b, status: BLOCK_STATUS.SKIPPED } : b))
        );
        break;
      }

      try {
        await cloneSingleBlock(i, initialBlocks);
        created++;
        setClonedCount(created);
      } catch (err) {
        setTemplateBlocks((prev) =>
          prev.map((b, idx) =>
            idx === i
              ? { ...b, status: BLOCK_STATUS.ERROR, error: err.message }
              : idx > i
                ? { ...b, status: BLOCK_STATUS.SKIPPED }
                : b
          )
        );
        throw err;
      }
    }

    return created;
  }, [requiredVersions, totalContactCount, selectedTemplate, cloneSingleBlock]);

  const startCampaignCreation = useCallback(async (created) => {
    setPhase(PHASES.CREATING);

    creatingTimeoutRef.current = setTimeout(() => {
      setErrorMessage('La creación de la campaña está tomando más tiempo de lo esperado.');
      setPhase(PHASES.ERROR);
    }, 60000);

    try {
      const result = await onCampaignCreated({ createdCount: created, hasClones: created > 0 });
      if (creatingTimeoutRef.current) {
        clearTimeout(creatingTimeoutRef.current);
        creatingTimeoutRef.current = null;
      }
      if (result?.success) {
        setCampaignName(result.campaignName || '');
        setPhase(PHASES.SUCCESS);
      } else {
        setErrorMessage(result?.error || 'Error al crear la campaña');
        setPhase(PHASES.ERROR);
      }
    } catch (err) {
      if (creatingTimeoutRef.current) {
        clearTimeout(creatingTimeoutRef.current);
        creatingTimeoutRef.current = null;
      }
      setErrorMessage(err.message || 'Error al crear la campaña');
      setPhase(PHASES.ERROR);
    }
  }, [onCampaignCreated]);

  const startProcess = useCallback(async () => {
    if (startedRef.current) return;
    startedRef.current = true;

    if (requiredVersions <= 1) {
      await startCampaignCreation(0);
      return;
    }

    setPhase(PHASES.CLONING);

    try {
      const created = await cloneAllTemplates();
      await startCampaignCreation(created);
    } catch (err) {
      setErrorMessage(err.message || 'Error durante la clonación de templates');
      setPhase(PHASES.ERROR);
    }
  }, [requiredVersions, startCampaignCreation, cloneAllTemplates]);

  useEffect(() => {
    if (open && !startedRef.current) {
      resetState();
      // Start process on next tick to allow state reset
      setTimeout(() => startProcess(), 50);
    }
  }, [open, resetState, startProcess]);

  const handleRetry = useCallback(async () => {
    const failedBlock = templateBlocks.find((b) => b.status === BLOCK_STATUS.ERROR);
    if (!failedBlock) {
      await startCampaignCreation(clonedCount);
      return;
    }

    setPhase(PHASES.CLONING);
    setErrorMessage('');

    const failedIndex = templateBlocks.findIndex((b) => b.status === BLOCK_STATUS.ERROR);

    try {
      await cloneSingleBlock(failedIndex, templateBlocks);
      const newCreated = clonedCount + 1;
      setClonedCount(newCreated);

      // Continue with skipped blocks
      for (let j = failedIndex + 1; j < templateBlocks.length; j++) {
        if (cancelCloneRef.current) break;
        try {
          await cloneSingleBlock(j, templateBlocks);
          setClonedCount((prev) => prev + 1);
        } catch (err) {
          setTemplateBlocks((prev) =>
            prev.map((b, idx) =>
              idx === j
                ? { ...b, status: BLOCK_STATUS.ERROR, error: err.message }
                : idx > j
                  ? { ...b, status: BLOCK_STATUS.SKIPPED }
                  : b
            )
          );
          setErrorMessage(err.message);
          setPhase(PHASES.ERROR);
          return;
        }
      }

      await startCampaignCreation(templateBlocks.length);
    } catch (err) {
      setErrorMessage(err.message);
      setPhase(PHASES.ERROR);
    }
  }, [templateBlocks, clonedCount, startCampaignCreation, cloneSingleBlock]);

  const handleCancel = useCallback(() => {
    cancelCloneRef.current = true;
    resetState();
    onClose?.();
  }, [onClose, resetState]);

  const handleClose = useCallback(() => {
    resetState();
    onClose?.();
  }, [onClose, resetState]);

  const handleComplete = useCallback(() => {
    resetState();
    onComplete?.();
  }, [onComplete, resetState]);

  const progress =
    templateBlocks.length > 0
      ? Math.round(
          (templateBlocks.filter((b) => b.status === BLOCK_STATUS.CREATED).length /
            templateBlocks.length) *
            100
        )
      : 0;

  const statusIcon = (status) => {
    switch (status) {
      case BLOCK_STATUS.CREATED:
        return <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />;
      case BLOCK_STATUS.ERROR:
        return <ErrorIcon sx={{ fontSize: 18, color: 'error.main' }} />;
      case BLOCK_STATUS.CREATING:
        return (
          <Typography variant="caption" sx={{ width: 18, textAlign: 'center', fontWeight: 700 }}>
            ...
          </Typography>
        );
      case BLOCK_STATUS.SKIPPED:
        return (
          <Typography variant="caption" sx={{ width: 18, textAlign: 'center', color: 'text.disabled' }}>
            -
          </Typography>
        );
      default:
        return (
          <Typography variant="caption" sx={{ width: 18, textAlign: 'center', color: 'text.disabled' }}>
            &bull;
          </Typography>
        );
    }
  };

  return (
    <Dialog open={open} maxWidth="sm" fullWidth onClose={phase === PHASES.CLONING ? undefined : handleClose}>
      {/* Header */}
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {phase === PHASES.SUCCESS && '¡Campaña creada exitosamente!'}
          {phase === PHASES.ERROR && 'Error al crear campaña'}
          {(phase === PHASES.IDLE || phase === PHASES.CLONING || phase === PHASES.CREATING) && 'Creando Campaña'}
        </Typography>
        {phase !== PHASES.CLONING && (
          <IconButton size="small" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent sx={{ pb: phase === PHASES.SUCCESS ? 1 : 3 }}>
        {/* Phase: CLONING */}
        {phase === PHASES.CLONING && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Typography variant="body2" color="primary" sx={{ fontWeight: 500 }}>
                Preparando templates...
              </Typography>
            </Box>

            <LinearProgress variant="determinate" value={progress} sx={{ mb: 2, height: 8, borderRadius: 4 }} />

            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Clon {templateBlocks.filter((b) => b.status === BLOCK_STATUS.CREATED).length + 1} de{' '}
              {templateBlocks.length}
            </Typography>

            {templateBlocks.map((block, idx) => (
              <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                {statusIcon(block.status)}
                <Typography
                  variant="body2"
                  color={block.status === BLOCK_STATUS.ERROR ? 'error.main' : 'text.primary'}
                  sx={{ fontSize: '0.8rem' }}
                >
                  Bloque {idx + 2}
                  {block.status === BLOCK_STATUS.CREATED && ' — Listo'}
                  {block.status === BLOCK_STATUS.ERROR && ` — ${block.error || 'Error'}`}
                  {block.status === BLOCK_STATUS.SKIPPED && ' — Omitido'}
                </Typography>
              </Box>
            ))}

            <Button variant="outlined" color="error" size="small" onClick={handleCancel} sx={{ mt: 2 }}>
              Cancelar
            </Button>
          </Box>
        )}

        {/* Phase: CREATING */}
        {phase === PHASES.CREATING && (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="body2" color="primary" sx={{ fontWeight: 500, mb: 2 }}>
              {templateBlocks.length > 0
                ? 'Templates listos. Creando campaña...'
                : 'Creando campaña...'}
            </Typography>

            <LinearProgress sx={{ mb: 2, height: 8, borderRadius: 4 }} />

            {templateBlocks.length > 0 && (
              <Typography variant="caption" color="text.secondary">
                {templateBlocks.length} {templateBlocks.length === 1 ? 'template preparado' : 'templates preparados'}
              </Typography>
            )}
          </Box>
        )}

        {/* Phase: SUCCESS */}
        {phase === PHASES.SUCCESS && (
          <Box sx={{ textAlign: 'center', py: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main' }} />
            </Box>

            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
              ¡Campaña creada exitosamente!
            </Typography>

            {campaignName && (
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                {campaignName}
              </Typography>
            )}

            <Box
              sx={{
                p: 2,
                backgroundColor: 'primary.lighter',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'primary.light',
                mb: 2,
                textAlign: 'left',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                <RocketLaunchIcon color="primary" sx={{ fontSize: 20, mt: 0.2 }} />
                <Typography variant="body2" color="primary.dark">
                  <strong>¿Deseas lanzar tu campaña ahora?</strong>
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ ml: 3.5 }}>
                Tu campaña está programada para enviarse en la fecha seleccionada.
                Si deseas lanzarla inmediatamente, puedes hacerlo desde la tabla de
                campañas usando el botón <strong>"Lanzar"</strong>.
              </Typography>
            </Box>

            <Typography variant="body2" color="text.secondary">
              Recuerda que puedes editar o eliminar tu campaña desde la tabla
              mientras tenga el estado <strong>"Programado"</strong>.
            </Typography>
          </Box>
        )}

        {/* Phase: ERROR */}
        {phase === PHASES.ERROR && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <ErrorIcon color="error" />
              <Typography variant="body2" color="error.main" sx={{ fontWeight: 500 }}>
                {errorMessage}
              </Typography>
            </Box>

            {templateBlocks.length > 0 && (
              <Box sx={{ mb: 1 }}>
                {templateBlocks.map((block, idx) => (
                  <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    {statusIcon(block.status)}
                    <Typography
                      variant="body2"
                      color={block.status === BLOCK_STATUS.ERROR ? 'error.main' : 'text.primary'}
                      sx={{ fontSize: '0.8rem' }}
                    >
                      Bloque {idx + 2}
                      {block.status === BLOCK_STATUS.CREATED && ' — Listo'}
                      {block.status === BLOCK_STATUS.ERROR && ` — ${block.error || 'Error'}`}
                      {block.status === BLOCK_STATUS.SKIPPED && ' — Pendiente'}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      {/* Actions */}
      <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'center' }}>
        {phase === PHASES.SUCCESS && (
          <Button onClick={handleComplete} variant="contained" size="large" sx={{ minWidth: 150 }}>
            Entendido
          </Button>
        )}

        {phase === PHASES.ERROR && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={handleCancel} variant="outlined" color="inherit">
              Cancelar
            </Button>
            {templateBlocks.some((b) => b.status === BLOCK_STATUS.ERROR) && (
              <Button onClick={handleRetry} variant="contained">
                Reintentar
              </Button>
            )}
            {clonedCount > 0 && !templateBlocks.some((b) => b.status === BLOCK_STATUS.ERROR) && (
              <Button onClick={handleRetry} variant="contained">
                Reintentar
              </Button>
            )}
          </Box>
        )}
      </DialogActions>
    </Dialog>
  );
};

CampaignCreationProgressModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onComplete: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onCampaignCreated: PropTypes.func.isRequired,
  selectedTemplate: PropTypes.shape({
    _id: PropTypes.string,
    name: PropTypes.string,
    category: PropTypes.string,
    language: PropTypes.string,
  }),
  requiredVersions: PropTypes.number.isRequired,
  totalContactCount: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  gupshupAppId: PropTypes.string.isRequired,
};

export default CampaignCreationProgressModal;
